import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useSort, { applySort } from '../../hooks/useSort';
import toast from 'react-hot-toast';
import adminApi, { errMsg } from '../../api';
import { useAdminAuth, hasRole } from '../../auth';
import { useRange, withRange } from '../../range';
import { usePageParams, pageMeta } from '../../paging';
import { Band, Card, Field } from '../../components/Card';
import DataTable from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import KpiTile from '../../components/KpiTile';
import PageHeader from '../../components/PageHeader';
import Pagination from '../../components/Pagination';
import StatusPill from '../../components/StatusPill';
import { ReasonDialog } from '../../components/Dialog';
import { formatCompactNumber, formatDateTime, formatPct, formatRelative } from '../../format';

/**
 * One brand, everything about it.
 *
 * This page is the answer to the complaint that started the rebuild: a
 * brand's life used to be split across four tabs — allowlist status on
 * Brands, engagement and funnel on Catalog, moderation on Products, job
 * history on Crawl — so "is this brand healthy?" meant visiting all four and
 * assembling the answer in your head. The build plan asked for exactly this
 * page in its Phase 5 and it was never built.
 *
 * Everything is one `GET /brand/{id}` call, so the funnel, the crawl status
 * and the product table all describe the same instant rather than three
 * moments a few hundred milliseconds apart.
 */
export default function BrandDetailPage() {
  const { brandId } = useParams();
  const navigate = useNavigate();
  const range = useRange();
  const { admin } = useAdminAuth();
  const canOperate = hasRole(admin, 'operator', 'owner');

  const { page, pageSize, setPage, setPageSize } = usePageParams();
  const { sort, onSort } = useSort();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState({});
  const [deboarding, setDeboarding] = useState(false);
  const [moderating, setModerating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await adminApi.get(`/brand/${encodeURIComponent(brandId)}`, {
        params: { days: range.days, page, page_size: pageSize },
      });
      setData(data);
    } catch (err) {
      setError(errMsg(err, 'Could not load this brand'));
    } finally {
      setLoading(false);
    }
  }, [brandId, range.days, page, pageSize]);

  useEffect(() => { load(); }, [load]);

  const triggerCrawl = async (sourceId) => {
    setTriggering((p) => ({ ...p, [sourceId]: true }));
    try {
      await adminApi.post('/ops/crawl/trigger', null, { params: { source_id: sourceId } });
      toast.success('Crawl queued');
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not trigger crawl'));
    } finally {
      setTriggering((p) => ({ ...p, [sourceId]: false }));
    }
  };

  const deboard = async (reason) => {
    try {
      await adminApi.post(`/ops/brands/${encodeURIComponent(brandId)}/deboard`, { reason });
      toast.success('Deboarded — pulled from the redirect allowlist, crawling stopped');
      setDeboarding(false);
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'Could not deboard'));
      setDeboarding(false);
    }
  };

  const moderate = async (reason) => {
    const { product, hidden } = moderating;
    try {
      await adminApi.patch(`/ops/products/${encodeURIComponent(product.mira_sku)}`, { hidden, reason });
      // `products` is now a pagination envelope, so the optimistic update
      // patches the current page's items rather than the object itself.
      setData((prev) => ({
        ...prev,
        products: {
          ...prev.products,
          items: (prev.products?.items || []).map(
            (p) => (p.mira_sku === product.mira_sku ? { ...p, hidden } : p),
          ),
        },
      }));
      toast.success(hidden ? 'Product hidden' : 'Product re-enabled');
      setModerating(null);
    } catch (err) {
      toast.error(errMsg(err, 'Could not update product'));
      setModerating(null);
    }
  };

  const productColumns = useMemo(() => {
    const cols = [
      {
        key: 'title',
        label: 'Product',
        width: '30%',
        render: (r) => (
          <div className="min-w-0">
            <div className="truncate font-medium text-ink" title={r.title}>{r.title || r.mira_sku}</div>
            <div className="truncate font-mono text-[10px] text-ink-muted">{r.mira_sku}</div>
          </div>
        ),
      },
      { key: 'category', label: 'Category' },
      { key: 'impressions', label: 'Impressions', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.impressions) },
      { key: 'saves', label: 'Saved', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.saves) },
      { key: 'passes', label: 'Passed', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.passes) },
      { key: 'save_rate_pct', label: 'Save rate', align: 'right', sortable: true, render: (r) => formatPct(r.save_rate_pct, 1) },
      { key: 'tryons', label: 'Try-ons', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.tryons) },
      { key: 'billable_clicks', label: 'Billable', align: 'right', sortable: true, render: (r) => formatCompactNumber(r.billable_clicks) },
      {
        key: 'hidden',
        label: 'Visibility',
        render: (r) => (r.hidden
          ? <StatusPill tone="critical" label="Hidden" />
          : <StatusPill tone="good" label="Visible" />),
      },
    ];
    if (canOperate) {
      cols.push({
        key: 'moderate',
        label: '',
        align: 'right',
        render: (r) => (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setModerating({ product: r, hidden: !r.hidden }); }}
            className={`rounded-md border px-2 py-1 text-[11px] font-medium ${
              r.hidden
                ? 'border-status-good/30 text-status-good hover:bg-status-good/10'
                : 'border-status-critical/30 text-status-critical hover:bg-status-critical/10'
            }`}
          >
            {r.hidden ? 'Re-enable' : 'Hide'}
          </button>
        ),
      });
    }
    return cols;
  }, [canOperate]);

  const sortedProducts = useMemo(
    () => applySort(data?.products?.items || [], sort, productColumns),
    [data, sort, productColumns],
  );

  if (loading && !data) return <Card><p className="text-sm text-ink-muted">Loading…</p></Card>;
  if (error) {
    return (
      <div>
        <PageHeader title="Brand" back={{ to: '/catalog', label: 'All brands' }} />
        <EmptyState title="Could not load this brand" detail={error} />
      </div>
    );
  }
  if (!data) return null;

  const eng = data.engagement || {};
  const funnel = eng.funnel_pct || {};

  return (
    <div>
      <PageHeader
        title={data.name}
        back={{ to: withRange('/catalog', range), label: 'All brands' }}
        hint={(data.domains || []).join(', ') || data.brand_id}
        actions={
          <div className="flex items-center gap-2">
            {data.onboarded
              ? (data.active
                  ? <StatusPill tone="good" label="Allowlisted" />
                  : <StatusPill tone="critical" label="Deboarded" />)
              : <StatusPill tone="warning" label="Not onboarded" />}
            {canOperate && data.onboarded && data.active && (
              <button
                onClick={() => setDeboarding(true)}
                className="rounded-lg border border-status-critical/30 px-2.5 py-1.5 text-xs font-medium text-status-critical hover:bg-status-critical/10"
              >
                Deboard
              </button>
            )}
          </div>
        }
      />

      {!data.onboarded && (
        <p className="mb-6 rounded-lg border border-status-warning/40 bg-status-warning/5 px-3.5 py-2.5 text-xs leading-relaxed text-[#8a5a00]">
          <span className="font-semibold">This brand is crawled but never onboarded.</span> It has no row in the
          buy-click redirect allowlist, so clicks through to it cannot be attributed or billed. Onboard it from
          the Brands tab using the same domain to fix that.
        </p>
      )}

      <Band title={`Engagement — ${range.label.toLowerCase()}`}>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <KpiTile label="Impressions" value={formatCompactNumber(eng.impressions)} />
          <KpiTile label="Saved" value={formatCompactNumber(eng.saves)} />
          <KpiTile label="Passed" value={formatCompactNumber(eng.passes)} />
          <KpiTile
            label="Billable clicks"
            value={formatCompactNumber(eng.billable_clicks)}
            caption="Deduplicated, bot-filtered. Not a purchase count."
          />
        </div>
      </Band>

      <Band title="Funnel">
        <Card>
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink-secondary">
            <span className="rounded-full bg-surface-page px-3 py-1.5">
              Impression → Save <span className="ml-1 font-semibold text-ink">{formatPct(funnel.impression_to_save, 1)}</span>
            </span>
            <span className="text-ink-muted">→</span>
            <span className="rounded-full bg-surface-page px-3 py-1.5">
              Save → Try-on <span className="ml-1 font-semibold text-ink">{formatPct(funnel.save_to_tryon, 1)}</span>
            </span>
            <span className="text-ink-muted">→</span>
            <span className="rounded-full bg-surface-page px-3 py-1.5">
              Try-on → Billable click <span className="ml-1 font-semibold text-ink">{formatPct(funnel.tryon_to_billable_click, 1)}</span>
            </span>
            <Link
              to={withRange(`/growth/funnel`, range)}
              className="ml-auto text-xs font-medium text-raspberry-600 hover:text-raspberry-700"
            >
              Compare against other brands →
            </Link>
          </div>
        </Card>
      </Band>

      <div className="mb-8 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card title="Crawl">
          {(data.crawl || []).length === 0 ? (
            <p className="text-sm text-ink-muted">No crawl source is linked to this brand.</p>
          ) : (
            <div className="space-y-3">
              {data.crawl.map((s) => {
                const busy = ['queued', 'running'].includes(s.last_job?.status);
                return (
                  <div key={s.source_id} className="flex items-start justify-between gap-3 border-b border-hairline pb-3 last:border-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-ink">{s.name}</div>
                      <div className="text-[11px] text-ink-muted">
                        {s.last_job
                          ? <>Last run {formatRelative(s.last_job.created_at)}
                              {s.last_job.result && ` · saved ${s.last_job.result.saved ?? 0}, deactivated ${s.last_job.result.deactivated ?? 0}`}</>
                          : 'Never run through this panel'}
                      </div>
                      {s.last_job?.error && (
                        <div className="mt-0.5 text-[11px] text-status-critical">{s.last_job.error}</div>
                      )}
                    </div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {s.last_job ? <StatusPill status={s.last_job.status} /> : <StatusPill tone="neutral" label="Never run" />}
                      {canOperate && (
                        <button
                          onClick={() => triggerCrawl(s.source_id)}
                          disabled={triggering[s.source_id] || busy}
                          className="rounded-md border border-hairline px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink disabled:opacity-40"
                        >
                          {busy ? 'In progress…' : 'Crawl now'}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card title="Account">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-4">
            <Field label="Brand id" mono>{data.brand_id}</Field>
            <Field label="Catalogue">
              {formatCompactNumber(eng.products_with_engagement)} with activity
              {eng.hidden_products > 0 && ` · ${eng.hidden_products} hidden`}
            </Field>
            <Field label="Affiliate param" mono>{data.affiliate_param_name || null}</Field>
            <Field label="UTM">
              {Object.keys(data.utm_params || {}).length
                ? Object.entries(data.utm_params).map(([k, v]) => `${k}=${v}`).join(' · ')
                : null}
            </Field>
            <Field label="Onboarded">{formatDateTime(data.created_at)}</Field>
            <Field label="Last edited">
              {data.updated_at ? `${formatRelative(data.updated_at)}${data.updated_by ? ` by ${data.updated_by}` : ''}` : null}
            </Field>
            {data.partner && (
              <Field label="Partner account">
                <span className="inline-flex items-center gap-2">
                  <StatusPill status={data.partner.status} />
                  <Link to="/catalog/applications" className="text-xs font-medium text-raspberry-600 hover:text-raspberry-700">
                    {data.partner.brand_name} →
                  </Link>
                </span>
              </Field>
            )}
          </dl>
          {data.partner?.contact_emails?.length > 0 && (
            <p className="mt-3 text-[11px] text-ink-muted">
              Contact: {data.partner.contact_emails.join(', ')}
            </p>
          )}
        </Card>
      </div>

      <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card title="Most saved">
          <ol className="space-y-1.5 text-xs">
            {(data.most_saved || []).filter((p) => p.saves > 0).map((p, i) => (
              <li key={p.mira_sku} className="flex items-baseline gap-2">
                <span className="w-4 flex-shrink-0 text-ink-muted">{i + 1}.</span>
                <Link to={`/product/${encodeURIComponent(p.mira_sku)}`} className="min-w-0 flex-1 truncate text-ink hover:text-raspberry-600">
                  {p.title || p.mira_sku}
                </Link>
                <span className="flex-shrink-0 tabular-nums text-ink-muted">{formatCompactNumber(p.saves)}</span>
              </li>
            ))}
            {(data.most_saved || []).filter((p) => p.saves > 0).length === 0 && (
              <li className="text-ink-muted">Nothing saved in this window.</li>
            )}
          </ol>
        </Card>
        <Card title="Most passed" hint="A pass means &ldquo;not this one, right now&rdquo; — a much weaker signal than dislike.">
          <ol className="space-y-1.5 text-xs">
            {(data.most_passed || []).filter((p) => p.passes > 0).map((p, i) => (
              <li key={p.mira_sku} className="flex items-baseline gap-2">
                <span className="w-4 flex-shrink-0 text-ink-muted">{i + 1}.</span>
                <Link to={`/product/${encodeURIComponent(p.mira_sku)}`} className="min-w-0 flex-1 truncate text-ink hover:text-raspberry-600">
                  {p.title || p.mira_sku}
                </Link>
                <span className="flex-shrink-0 tabular-nums text-ink-muted">{formatCompactNumber(p.passes)}</span>
              </li>
            ))}
            {(data.most_passed || []).filter((p) => p.passes > 0).length === 0 && (
              <li className="text-ink-muted">Nothing passed in this window.</li>
            )}
          </ol>
        </Card>
      </div>

      <Band title="Products with activity">
        <DataTable
          columns={productColumns}
          rows={sortedProducts}
          rowKey={(r) => r.mira_sku}
          loading={loading}
          onRowClick={(r) => navigate(withRange(`/product/${encodeURIComponent(r.mira_sku)}`, range))}
          dense
          sort={sort}
          onSort={onSort}
          empty={
            <EmptyState
              title="No product engagement in this window"
              detail="Only products someone saw appear here. The brand may still have a full catalogue."
            />
          }
        />
        <Pagination
          meta={pageMeta(data.products)}
          loading={loading}
          onPage={setPage}
          onPageSize={setPageSize}
        />
      </Band>

      <ReasonDialog
        open={deboarding}
        tone="critical"
        title={`Deboard ${data.name}?`}
        body="This pulls the domain from the buy-click redirect allowlist and stops crawling. Existing links fall back in-app rather than breaking, and their products stay in anyone's Wardrobe who already saved them."
        confirmLabel="Deboard brand"
        onConfirm={deboard}
        onClose={() => setDeboarding(false)}
      />

      <ReasonDialog
        open={Boolean(moderating)}
        tone={moderating?.hidden ? 'critical' : 'default'}
        title={moderating?.hidden
          ? `Hide "${moderating?.product?.title || moderating?.product?.mira_sku}"?`
          : `Re-enable "${moderating?.product?.title || moderating?.product?.mira_sku}"?`}
        body={moderating?.hidden
          ? 'It stops appearing in discovery, search and anything the stylist can recommend. People who already saved it keep seeing it in their Wardrobe.'
          : 'It becomes eligible for discovery again, provided the crawler still lists it as available.'}
        confirmLabel={moderating?.hidden ? 'Hide product' : 'Re-enable product'}
        onConfirm={moderate}
        onClose={() => setModerating(null)}
      />
    </div>
  );
}
